import { Trans, useTranslation } from 'next-i18next'

import { isAuthenticated } from '~/auth/slices/auth.slice'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { Link } from '~/components/Link'
import appConfig from '~/config/app'
import { useHasCampaignLeaderboardQuery } from '~/Leaderboard/apis/leaderboard-api'
import LeagueBadge from '~/Leaderboard/components/LeagueBadge'
import { formatTimeFromSec, getLeagueName } from '~/Leaderboard/utils/utils'
import { useAppSelector } from '~/redux/hooks'
import ROUTES from '~/shared/constants/routes.const'

export default function LeaderboardWrapper({
  children,
  leaderboardCapacity,
  timeToReset,
  currentLeague,
  highestLeague,
  promotionSettings,
  notParticipating,
}: {
  children: React.ReactElement
  timeToReset: number
  leaderboardCapacity: number
  currentLeague: number
  highestLeague: number
  promotionSettings?: { promote: number; demote: number }
  notParticipating?: boolean
}): React.ReactElement {
  const { t } = useTranslation('leaderboard')
  const timeText = { day: t('days'), hour: t('hours'), min: t('minutes') }
  const formatTime = formatTimeFromSec({ seconds: timeToReset, timeText })
  const nrOfPromotions = promotionSettings?.promote || 0
  const nrOfDemotions = promotionSettings?.demote || 0
  const nrOfStays = leaderboardCapacity - nrOfPromotions - nrOfDemotions
  const leagueName = getLeagueName(currentLeague)
  const authenticated = useAppSelector(isAuthenticated)
  const hasCampaignAccess = useHasCampaignLeaderboardQuery()
  // The route itself is blocked in the school platform; this stops the entry point rendering a link
  // to a 404 if a school account ever came back as a campaign participant.
  const isAccentureUser = hasCampaignAccess?.data?.campaignName === 'Accenture' && !appConfig.isSchoolPlatform

  return (
    <div className="flex flex-col w-full min-h-screen h-full text-white gap-4 z-10">
      {isAccentureUser && (
        <Link
          className="flex items-end text-md mx-auto border border-white/50 px-8 py-2 mt-4 rounded hover:scale-110 duration-300"
          to={ROUTES.ACCENTURE_LEADERBOARD}
        >
          <p className="leading-none mr-1.5">{t('to')}</p>
          <ResponsiveImage
            className="pb-0.5 mb-px"
            height={26}
            srcKey="donation/sponsors/accenture-all-white.png"
            width={100}
          />
          <p className="leading-none ml-1.5">{t('leaderboard')}</p>
        </Link>
      )}
      <div className={`gap-4 ${isAccentureUser ? '' : 'mt-5'}`}>
        <div className="text-3xl font-semibold text-center mx-auto sm:w-auto">
          {notParticipating ? t('notParticipating') : t(`league.${leagueName}`)}
        </div>

        <div className="overflow-hidden pt-2">
          <LeagueBadge currentLeague={currentLeague} highestLeague={highestLeague} />
        </div>
        <div className="w-full mx-auto">
          {authenticated && (
            <div className="flex flex-col text-md font-light text-center clear-both">
              {currentLeague === 5 ? (
                <p>
                  {Trans({
                    t,
                    i18nKey: 'secureSpotInTop',
                    children: t('secureSpotInTop'),
                    values: { nrOfStays },
                  })}
                </p>
              ) : (
                <p>
                  {Trans({
                    t,
                    i18nKey: 'topAdvances',
                    children: t('topAdvances'),
                    values: { nrOfPromotions },
                  })}
                </p>
              )}
              <span
                className="text-orange-logo font-bold text-lg"
                style={{ WebkitTextStroke: '1px #2D184C', textShadow: '0px 3px 4px #2D184C' }}
              >
                {formatTime}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="w-full max-w-160 mx-auto">{children}</div>
      <div className="self-end border-b-2 border-white h-16 w-screen pt-5 mt-auto" />
    </div>
  )
}
